import { useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

/**
 * Mapping dry-run: applies the in-progress spec to the first N rows of the
 * upload and shows, per row, the source data → the produced FHIR resource (or
 * the per-field errors), WITHOUT writing anything. The tight feedback loop for
 * the visual mapper — change a mapping, re-run, watch the errors drop.
 */
export function MappedDryRun({
  sessionId,
  uploadId,
  spec,
  limit = 10,
}: {
  sessionId: string;
  uploadId: string;
  spec: Record<string, unknown>;
  limit?: number;
}) {
  const run = useMutation({ mutationFn: () => api.dryRunFhirSpec(sessionId, { uploadId, spec, limit }) });

  return (
    <div className="space-y-2 text-[12px]">
      <div className="flex items-center gap-3">
        <button
          className="rounded px-2 py-1"
          style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-primary)', opacity: run.isPending ? 0.6 : 1 }}
          disabled={run.isPending}
          onClick={() => run.mutate()}
        >
          {run.isPending ? 'Running…' : `Preview first ${limit} rows`}
        </button>
        {run.data ? (
          <span className="text-[11px]">
            <span style={{ color: 'var(--accent-green, #3fb950)' }}>{run.data.okCount} ok</span>
            {run.data.errorCount > 0 ? <span style={{ color: 'var(--accent-red)' }}> · {run.data.errorCount} with errors</span> : null}
          </span>
        ) : null}
      </div>

      {run.error ? <div className="text-[11px]" style={{ color: 'var(--accent-red)' }}>Error: {String(run.error)}</div> : null}

      <div className="space-y-1">
        {(run.data?.rows ?? []).map((r) => (
          <div key={r.rowNumber} className="rounded-lg p-2" style={{ border: '1px solid var(--border)' }}>
            <div className="mb-1 flex items-center gap-2 text-[10px]" style={{ color: 'var(--text-dimmed)' }}>
              <span>row {r.rowNumber}</span>
              {r.errors.length > 0 ? <span style={{ color: 'var(--accent-red)' }}>· {r.errors.length} error(s)</span> : <span style={{ color: 'var(--accent-green, #3fb950)' }}>· valid</span>}
            </div>
            <div className="grid gap-2" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <pre className="overflow-x-auto text-[10px] font-mono" style={{ color: 'var(--text-dimmed)' }}>
                {JSON.stringify(r.sourceRow, null, 1)}
              </pre>
              <pre className="overflow-x-auto text-[10px] font-mono" style={{ color: r.resource ? 'var(--text-primary)' : 'var(--accent-red)' }}>
                {r.resource ? JSON.stringify(r.resource, null, 1) : '(no resource — see errors)'}
              </pre>
            </div>
            {r.errors.length > 0 ? (
              <ul className="mt-1 text-[10px]" style={{ color: 'var(--accent-red)' }}>
                {r.errors.map((e, i) => (
                  <li key={i}>• {e.path}: {e.detail}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
